import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { Typography, Select, DatePicker, Table, Tag, Space, Segmented, Checkbox, Popover, Button, Dropdown, message } from 'antd';
import AgentLink from '../components/AgentLink';
import AbsenceTag from '../components/AbsenceTag';
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import api from '../api';
import { UserContext } from '../App';
import ComplianceCreateModal from '../components/ComplianceCreateModal';

const PRIORITY = ['Break', 'Lunch', 'Unavailable', 'Refused Contact', 'Technical Issue'];

// Whether a duration breaches its configured threshold. Tiers win when any are
// configured -- they express "how much break a shift this long earns", which a
// flat cap cannot -- and a tiered status with no worked hours to place the
// agent in a tier is not a violation, it is unknown.
//
// Minutes only. The occurrence count is a CCaaS concept; the AWS export records
// one duration per state per day and nothing about how it was reached.
function exceedsMins(thresh, mins, workedMins) {
  if (!thresh || mins == null) return false;
  const tiers = (thresh.Tiers || []).slice().sort((a, b) => a.WorkedMinsMax - b.WorkedMinsMax);
  if (tiers.length > 0) {
    if (workedMins == null) return false;
    const tier = tiers.find((t) => workedMins < t.WorkedMinsMax);
    return tier ? mins > tier.MaxBreakMins : false;
  }
  return thresh.MaxMinsDay != null && mins > thresh.MaxMinsDay;
}

function isScheduleFlagged(r, thresholds, source = 'max') {
  // AWS source only. The Max view has no lunch column yet, and making its
  // filter answer a question it does not show would be a checkbox that hides
  // rows for a reason the reader cannot see.
  if (source === 'aws' && lunchOverage(r, thresholds).state === 'over') return true;

  const workingPunches = r.Punches?.filter(p => WORKING_CODES.has(p.code)) ?? [];
  const sessions       = r.LoginSessions ?? [];

  // Has working punches but never logged into IC
  if (workingPunches.length && !sessions.length) return true;

  // IC Available late or missing after a login
  const availMaxMins = thresholds['IC Login to IC Available']?.MaxValue ?? IC_LOGIN_TO_AVAIL_MAX_MINS;
  for (const s of sessions) {
    if (!s.available) return true;
    const delta = timeToMins(s.available) - timeToMins(s.login);
    if (delta != null && delta > availMaxMins) return true;
  }

  // IC Logout: last punch-out significantly after last IC logout (skip midnight splits)
  const lastPunchOut = workingPunches.length
    ? [...workingPunches].sort((a, b) => (a.out || '').localeCompare(b.out || '')).at(-1)?.out
    : null;
  if (lastPunchOut?.slice(0, 5) !== '23:59') {
    const lastSession = sessions.at(-1);
    if (lastSession?.logout && lastPunchOut) {
      const logoutMaxMins = thresholds['Punch Out after IC Logout']?.MaxValue ?? PUNCH_OUT_AFTER_LOGOUT_MAX_MINS;
      const delta = timeToMins(lastPunchOut) - timeToMins(lastSession.logout);
      if (delta > logoutMaxMins) return true;
    }
  }

  // Worked vs IC gap
  if (r.Worked && r.TotalICMins != null) {
    const gapMaxMins = thresholds['Punch IC Gap']?.MaxValue ?? PUNCH_IC_GAP_MAX_MINS;
    const gap = Math.round((r.Worked * 60) - r.TotalICMins);
    if (gap > gapMaxMins) return true;
  }

  // Punch-in to IC Available gap (early punch abuse)
  const punchMaxMins = thresholds['Punch to IC Available']?.MaxValue ?? PUNCH_TO_IC_MAX_MINS;
  for (const p of workingPunches.filter(p => p.in)) {
    const pMins      = timeToMins(p.in);
    const alreadyAvail = sessions.some(s =>
      s.available &&
      timeToMins(s.available) <= pMins &&
      (!s.logout || pMins <= timeToMins(s.logout))
    );
    if (alreadyAvail) continue;
    const nextAvail = sessions
      .filter(s => s.available && timeToMins(s.available) > pMins)
      .sort((a, b) => timeToMins(a.available) - timeToMins(b.available))[0]?.available ?? null;
    if (!nextAvail) continue;
    if (timeToMins(nextAvail) - pMins > punchMaxMins) return true;
  }

  return false;
}

const fmtPunch = (v) => (!v || v === '00:00:00') ? '—' : v.slice(0, 5);

// Clock times on the page read as AM/PM. Only on the page: the export keeps
// 24-hour, because a spreadsheet sorts "10:00 AM" after "1:00 PM" as text and
// the file exists to be sorted and filtered.
//
// Not to be confused with fmtTimeStr below, which formats a DURATION. A
// duration has no meridiem and running it through this would turn 13 minutes
// of handle time into one in the afternoon.
//
// Midnight is a time, not a missing value: 00:00 renders as 12:00 AM. Only an
// absent value gives a dash. fmtPunch, which the export still uses, does treat
// 00:00:00 as unset -- so a midnight punch reads 12:00 AM on screen and blank
// in the file. Worth knowing if that ever turns out to be real data.
const fmtClock = (v) => {
  if (!v) return '—';
  const [hStr, mStr] = String(v).split(':');
  const h = Number(hStr);
  if (Number.isNaN(h)) return v;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mStr ?? '00').padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const fmtHrs   = (v) => (v != null && v !== 0) ? Number(v).toFixed(2) : '—';

function fmtSecs(s) {
  if (s == null) return null;
  const t   = Math.round(s);
  const h   = Math.floor(t / 3600);
  const m   = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${m}:${String(sec).padStart(2,'0')}`;
}

function fmtTimeStr(s) {
  if (!s) return null;
  const [h, m, sec] = s.split(':').map(Number);
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${m}:${String(sec).padStart(2,'0')}`;
}

function timeStrToSecs(s) {
  if (!s) return null;
  const parts = String(s).split(':');
  if (parts.length === 3) return (+parts[0]) * 3600 + (+parts[1]) * 60 + (+parts[2]);
  if (parts.length === 2) return (+parts[0]) * 60  + (+parts[1]);
  return null;
}

function timeToMins(s) {
  if (!s) return null;
  const parts = String(s).split(':');
  return (+parts[0]) * 60 + (+parts[1]);
}

const IC_LOGIN_TO_AVAIL_MAX_MINS       = 5;
const PUNCH_TO_IC_MAX_MINS            = 5;
const PUNCH_OUT_AFTER_LOGOUT_MAX_MINS = 2;
const PUNCH_IC_GAP_MAX_MINS           = 15;
const LUNCH_OVERAGE_MAX_MINS          = 1;

/**
 * Lunch taken in the CCaaS against lunch punched out of the timecard.
 *
 * Returns { state, mins }:
 *   over     lunch exceeds the unpaid punch gap by more than the tolerance
 *   ok       it does not
 *   unknown  lunch was taken but the punches cannot say how much was unpaid
 *   none     no lunch to account for
 *
 * The comparison is only safe on the simplest day there is: one scheduled
 * shift and exactly two working punch pairs, where the gap between them is
 * unambiguously the lunch. Split shifts, a mid-day absence or any other
 * pattern produce gaps that are not lunch, and reading one as lunch would
 * accuse someone on the strength of a guess.
 *
 * Midnight shifts are excluded outright. A shift crossing midnight is punched
 * as two pairs split at 23:59/00:00, which passes the two-pair test while the
 * "gap" between them is a minute of day boundary rather than a break. Without
 * this, every night shift reports nearly its whole lunch as unpunched. The
 * 23:59 punch-out is the same tell isScheduleFlagged already uses.
 */
function lunchOverage(r, thresholds) {
  const lunch = r.LunchMins;
  if (lunch == null || lunch <= 0) return { state: 'none', mins: 0 };

  const schedules = r.Schedules ?? [];
  const punches = (r.Punches ?? [])
    .filter((p) => WORKING_CODES.has(p.code) && p.in && p.out)
    .sort((a, b) => (a.in || '').localeCompare(b.in || ''));

  const midnightSplit = punches.some((p) => p.out?.slice(0, 5) === '23:59');
  if (schedules.length !== 1 || punches.length !== 2 || midnightSplit) {
    return { state: 'unknown', mins: 0 };
  }

  const gap = timeToMins(punches[1].in) - timeToMins(punches[0].out);
  if (gap == null || Number.isNaN(gap) || gap < 0) return { state: 'unknown', mins: 0 };

  const over = Math.round(lunch - gap);
  const maxMins = thresholds['CCaaS Lunch vs Timecard Lunch']?.MaxValue ?? LUNCH_OVERAGE_MAX_MINS;
  return over > maxMins ? { state: 'over', mins: over } : { state: 'ok', mins: over };
}
const WORKING_CODES                   = new Set(['Reg', 'OT', 'Hol1.5']);

const dash = <span style={{ color: '#d9d9d9' }}>—</span>;
const T    = (v) => v != null ? v : dash;
const TS   = (v) => { const f = fmtSecs(v); return f != null ? f : dash; };

// AWS State by Day columns: same shape as the CCaaS pivot, minus what the
// source cannot say: no occurrence count, and no popover of start and end
// times. A cell is a duration and nothing else. Shared with Agent Summary >
// AWS States, where perDay puts one agent's days in the rows.
export function buildAwsStateCols({ navigate, currentUser, date, setComplianceModal, states, threshFor, workedMinsFor, perDay = false }) {
  const dOf = (row) => (perDay && row?.Date ? dayjs(row.Date) : date);
  return [
  perDay ? {
    title: 'Date', dataIndex: 'Date', key: 'Date', width: 120, fixed: 'left',
    sorter: (a, b) => a.Date.localeCompare(b.Date),
  } : {
    title: 'Agent', dataIndex: 'DisplayName', key: 'agent', width: 200, fixed: 'left',
    sorter: (a, b) => a.DisplayName.localeCompare(b.DisplayName),
    render: (name, row) => (
      <AgentLink
        name={name}
        jobName={row.JobName}
        url={`/agents?user_id=${row.UserID}&tab=aws-calls`}
        navigate={navigate}
        canNavigate={(currentUser?.CanViewAgentSummary ?? 0) >= 1}
      />
    ),
  },
  {
    title: 'Contacts', dataIndex: 'TotalContactsHandled', key: 'contacts', width: 100, align: 'right',
    sorter: (a, b) => (a.TotalContactsHandled ?? -1) - (b.TotalContactsHandled ?? -1),
    render: (v) => v == null ? <span style={{ color: '#d9d9d9' }}>—</span> : v.toLocaleString(),
  },
  {
    title: 'Occupancy', dataIndex: 'OccupancyPct', key: 'occ', width: 105, align: 'right',
    sorter: (a, b) => (a.OccupancyPct ?? -1) - (b.OccupancyPct ?? -1),
    render: (v) => v == null ? <span style={{ color: '#d9d9d9' }}>—</span> : `${Number(v).toFixed(2)}%`,
  },
  ...states.map((state) => {
    const thresh = threshFor(state);
    return {
      title: state, key: state, width: 120, align: 'right',
      sorter: (a, b) => (a[state] ?? -1) - (b[state] ?? -1),
      render: (_, row) => {
        const mins = row[state];
        if (mins == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
        const violation = exceedsMins(thresh, mins, workedMinsFor(row));
        const canRaise  = (currentUser?.CanEditIncidents ?? 0) >= 2;
        const tag = (
          <Tag color={violation ? 'red' : undefined}
            style={{ margin: 0, cursor: canRaise ? 'pointer' : 'default', fontWeight: violation ? 700 : undefined }}>
            {mins}m
          </Tag>
        );
        // Unlike the CCaaS cell there are no start and end times to show, so
        // without the permission to raise an incident the popover would be
        // an empty box on hover.
        if (!canRaise) return tag;
        return (
          <Popover
            trigger="hover"
            mouseLeaveDelay={0.3}
            content={(
              <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
                onClick={() => setComplianceModal({
                  agent:       { UserID: row.UserID, DisplayName: row.DisplayName },
                  day:         dOf(row),
                  description: `AWS: ${state} - ${mins} min on ${dOf(row).format('M/D/YY (ddd)')}`,
                })}>
                Create Compliance Incident
              </Button>
            )}
          >
            {tag}
          </Popover>
        );
      },
    };
  }),
  ];
}

// ── Schedule columns ────────────────────────────────────────────────────────
// opts.perDay: one agent, one row per day (Agent Summary > AWS Adherence).
// The first column is the row's Date instead of the agent, and each row's own
// date stands in for the page's single one.
export function buildScheduleCols(navigate, currentUser, date, setComplianceModal, thresholds = {}, source = 'max', opts = {}) {
  const perDay = !!opts.perDay;
  const dOf = (row) => (perDay && row?.Date ? dayjs(row.Date) : date);
  // IC means inContact, which is where the Max source reads its logins from.
  // The AWS source reads them from Salesforce and its lunch from the AWS daily
  // export, so calling either of those IC would name the wrong system.
  const isAws     = source === 'aws';
  const loginLbl  = isAws ? 'SFDC Login'  : 'IC Login';
  const logoutLbl = isAws ? 'SFDC Logout' : 'IC Logout';
  const hrsLbl    = isAws ? 'SFDC Hrs'    : 'IC Hrs';
  const gapLbl    = isAws ? 'Worked/SFDC Gap' : 'Worked/IC Gap';

  const sPop = (row, hasValue, displayContent, description) => {
    if (!hasValue) return displayContent;
    if ((currentUser?.CanEditIncidents ?? 0) < 2) return displayContent;
    const popContent = (
      <div style={{ fontSize: 13 }}>
        <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
          onClick={() => setComplianceModal({
            agent: { UserID: row.UserID, DisplayName: row.AgentName },
            day: dOf(row),
            description,
          })}>
          Create Compliance Incident
        </Button>
      </div>
    );
    return (
      <Popover content={popContent} trigger="hover" mouseLeaveDelay={0.3}>
        <span style={{ cursor: 'pointer' }}>{displayContent}</span>
      </Popover>
    );
  };

  return [
  perDay ? {
    title: 'Date', dataIndex: 'Date', key: 'Date', fixed: 'left', width: 120,
    render: (v, r) => (
      <div>
        <div>{v}</div>
        <AbsenceTag absences={r.Absences} navigate={navigate} canOpen={(currentUser?.CanEditAbsences ?? 0) >= 1 || r.UserID === currentUser?.ID} />
      </div>
    ),
  } : {
    title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', fixed: 'left', width: 160,
    render: (v, r) => (
      <AgentLink name={v} jobName={r.JobName} url={`/agents?user_id=${r.UserID}&tab=ccaas-status&ccaas_view=timeline&date=${date?.format('YYYY-MM-DD')}`} navigate={navigate} canNavigate={(currentUser?.CanViewAgentSummary ?? 0) >= 1}
        badge={<AbsenceTag absences={r.Absences} navigate={navigate} canOpen={(currentUser?.CanEditAbsences ?? 0) >= 1} />} />
    ),
  },
  {
    title: 'Shift', key: 'Shift', width: 150,
    render: (_, r) => r.Schedules?.length ? r.Schedules.map((s, i) => <div key={i}>{s.Shift || '—'}</div>) : '—',
  },
  {
    title: 'Sched Start', key: 'SchedStart', width: 115,
    render: (_, r) => r.Schedules?.length ? r.Schedules.map((s, i) => <div key={i}>{fmtClock(s.SchedStart)}</div>) : '—',
  },
  {
    title: 'Sched End', key: 'SchedEnd', width: 105,
    render: (_, r) => r.Schedules?.length ? r.Schedules.map((s, i) => (
      <div key={i}>
        {fmtClock(s.SchedEnd)}
        {s._schedEndNextDay && <span style={{ color: '#fa8c16', fontSize: 11 }}> (+1)</span>}
      </div>
    )) : '—',
  },
  {
    title: loginLbl, dataIndex: 'LoginSessions', key: 'LoginTimes', width: 110,
    render: (sessions, r) => {
      const display = sessions?.length ? sessions.map((s, i) => <div key={i}>{fmtClock(s.login)}</div>) : '—';
      return sPop(r, !!sessions?.length, display,
        `${loginLbl}: ${sessions?.map(s => fmtClock(s.login)).join(', ')} on ${dOf(r)?.format('M/D/YY (ddd)')}`);
    },
  },
  ...(source === 'aws' ? [] : [
    {
      title: 'IC Available', dataIndex: 'LoginSessions', key: 'FirstAvailable', width: 155,
      render: (sessions, r) => {
        const hasPunch = r.Punches?.some(p => WORKING_CODES.has(p.code));

        if (hasPunch && !sessions?.length) {
          const display = <Tag color="red" style={{ margin: 0 }}>No IC Activity</Tag>;
          return sPop(r, true, display,
            `No IC activity despite working punches on ${dOf(r)?.format('M/D/YY (ddd)')}`);
        }
        if (!sessions?.length) return dash;

        const maxMins = thresholds['IC Login to IC Available']?.MaxValue ?? IC_LOGIN_TO_AVAIL_MAX_MINS;
        const display = sessions.map((s, i) => {
          const f         = s.available ? fmtClock(s.available) : null;
          const delta     = s.available && s.login ? timeToMins(s.available) - timeToMins(s.login) : null;
          const violation = delta != null && delta > maxMins;
          if (!f) return <div key={i}><Tag color="red" style={{ margin: 0 }}>No Avail</Tag></div>;
          if (violation) return (
            <div key={i}>
              <Tag color="red" style={{ margin: 0 }}>
                {f} <span style={{ fontWeight: 400 }}>(+{delta}m)</span>
              </Tag>
            </div>
          );
          return <div key={i}>{f}</div>;
        });
        const anyViolation = sessions.some(s => {
          const delta = s.available && s.login ? timeToMins(s.available) - timeToMins(s.login) : null;
          return delta != null && delta > maxMins || !s.available;
        });
        return sPop(r, anyViolation, <div>{display}</div>,
          `IC Available: ${sessions.map(s => s.available ?? 'none').join(', ')} on ${dOf(r)?.format('M/D/YY (ddd)')}`);
      },
    },
  ]),
  {
    title: logoutLbl, dataIndex: 'LoginSessions', key: 'LogoutTimes', width: 130,
    render: (sessions, r) => {
      const display      = sessions?.length ? sessions.map((s, i) => (
        <div key={i}>{s.logout ? fmtClock(s.logout) : '—'}{s.logout && s.logout_next_day ? <span style={{ color: '#fa8c16', fontSize: 11 }}> (+1)</span> : null}</div>
      )) : '—';
      const lastSession  = sessions?.length ? sessions.at(-1) : null;
      const lastLogout   = lastSession?.logout ?? null;
      const lastPunchOut = r.Punches?.length
        ? [...r.Punches].filter(p => WORKING_CODES.has(p.code)).sort((a, b) => (a.out || '').localeCompare(b.out || '')).at(-1)?.out
        : null;

      // Midnight split: last punch at 23:59 — can't reliably compare, show advisory label
      if (lastPunchOut?.slice(0, 5) === '23:59') {
        return (
          <>
            {display}
            <Tag color="gold" style={{ margin: '2px 0 0', display: 'block' }}>Midnight Shift</Tag>
          </>
        );
      }

      if (lastLogout && lastPunchOut) {
        const maxMins = thresholds['Punch Out after IC Logout']?.MaxValue ?? PUNCH_OUT_AFTER_LOGOUT_MAX_MINS;
        const delta   = timeToMins(lastPunchOut) - timeToMins(lastLogout);
        if (delta > maxMins) {
          const flagDisplay = (
            <>
              {display}
              <Tag color="red" style={{ margin: '2px 0 0', display: 'block' }}>punch out +{delta}m</Tag>
            </>
          );
          return sPop(r, true, flagDisplay,
            `Last punch-out ${delta} min after ${logoutLbl} on ${dOf(r)?.format('M/D/YY (ddd)')}`);
        }
      }

      return sPop(r, !!lastLogout, display,
        `${logoutLbl}: ${sessions?.map(s => s.logout ?? '—').join(', ')} on ${dOf(r)?.format('M/D/YY (ddd)')}`);
    },
  },
  {
    title: 'Punches', dataIndex: 'Punches', key: 'Punches', width: 170,
    render: (v, r) => {
      const punchKey = (p) => (p._nextDay ? 1440 : 0) + timeToMins(p.in || '00:00');
      const sorted   = v?.length ? [...v].sort((a, b) => punchKey(a) - punchKey(b)) : null;
      const sessions = r.LoginSessions ?? [];
      const punchMaxMins = thresholds['Punch to IC Available']?.MaxValue ?? PUNCH_TO_IC_MAX_MINS;

      const getPunchGap = (p) => {
        if (!WORKING_CODES.has(p.code) || !p.in) return null;
        const pMins = timeToMins(p.in);
        // already IC Available at punch-in time — no gap to measure
        const alreadyAvail = sessions.some(s =>
          s.available &&
          timeToMins(s.available) <= pMins &&
          (!s.logout || pMins <= timeToMins(s.logout))
        );
        if (alreadyAvail) return null;
        // find the first IC Available after this punch-in
        const nextAvail = sessions
          .filter(s => s.available && timeToMins(s.available) > pMins)
          .sort((a, b) => timeToMins(a.available) - timeToMins(b.available))[0]?.available ?? null;
        if (!nextAvail) return null;
        return timeToMins(nextAvail) - pMins;
      };

      const anyPunchViolation = sorted?.some(p => {
        const gap = getPunchGap(p);
        return gap != null && gap > punchMaxMins;
      }) ?? false;

      const display = sorted
        ? sorted.map((p, i) => {
            const gap       = getPunchGap(p);
            const violation = gap != null && gap > punchMaxMins;
            return (
              <div key={i}>
                {p.in ? fmtClock(p.in) : '—'} - {p.out ? fmtClock(p.out) : '—'}
                {p._nextDay && <span style={{ color: '#fa8c16', fontSize: 11 }}> (+1)</span>}
                {p.code ? <span style={{ color: '#8c8c8c', fontSize: 11, marginLeft: 4 }}>({p.code})</span> : null}
                {violation && <Tag color="red" style={{ margin: '2px 0 0', display: 'block' }}>+{gap}m to IC Avail</Tag>}
              </div>
            );
          })
        : '—';

      const desc = sorted
        ? `Punches: ${sorted.map((p) => `${p.in?.slice(0,5) ?? '?'}-${p.out?.slice(0,5) ?? '?'}${p.code ? ` (${p.code})` : ''}`).join(', ')} on ${dOf(r)?.format('M/D/YY (ddd)')}`
        : '';
      return sPop(r, anyPunchViolation, display, desc);
    },
  },
  // AWS source only: the figure taken off the logged-in time to get SFDC Hrs.
  // Named for where it comes from -- the AWS daily export -- since the logins
  // beside it come from Salesforce and the two are different files.
  ...(source !== 'aws' ? [] : [{
    title: 'AWS Lunch', dataIndex: 'LunchMins', key: 'LunchMins', width: 100, align: 'right',
    sorter: (a, b) => (a.LunchMins ?? -1) - (b.LunchMins ?? -1),
    render: (v, r) => {
      // No lunch recorded is not the same as a zero-minute lunch, and neither
      // is an agent with no daily stats row for the day at all.
      if (v == null) return dash;
      const mins = Math.round(v);
      const { state, mins: over } = lunchOverage(r, thresholds);
      const display = (
        <div style={{ lineHeight: 1.4 }}>
          <div>{mins}m</div>
          {state === 'over' && (
            <Tag color="red" style={{ margin: 0, whiteSpace: 'normal' }}>
              {over}m more AWS Lunch vs Timecard
            </Tag>
          )}
          {/* Said rather than left blank: an empty cell here reads as nothing
              to see, when the truth is that nobody can tell from these
              punches. */}
          {state === 'unknown' && (
            <Tag color="gold" style={{ margin: 0, whiteSpace: 'normal' }}>
              cannot determine lunch overage
            </Tag>
          )}
        </div>
      );
      const why = state === 'over'
        ? `AWS Lunch ${mins}m exceeds the punched-out gap by ${over}m`
        : state === 'unknown'
          ? 'Lunch was taken, but the shift and punch pattern cannot say how much of it was punched out'
          : `AWS Lunch: ${mins}m, already subtracted from ${hrsLbl}`;
      return sPop(r, state === 'over', display, `${why} on ${dOf(r)?.format('M/D/YY (ddd)')}`);
    },
  }]),
  {
    title: gapLbl, key: 'PunchICGap', width: 130, align: 'right',
    render: (_, r) => {
      if (r.TotalICMins == null) return dash;
      const icHrs   = (r.TotalICMins / 60).toFixed(2);
      const gap     = r.Worked ? Math.round((r.Worked * 60) - r.TotalICMins) : null;
      const maxMins = thresholds['Punch IC Gap']?.MaxValue ?? PUNCH_IC_GAP_MAX_MINS;
      const violation = gap != null && gap > maxMins;
      const display = (
        <div style={{ lineHeight: '1.4' }}>
          <div>{hrsLbl}: {icHrs}</div>
          {gap != null && gap > 0 && (
            violation
              ? <Tag color="red" style={{ margin: 0 }}>{gap}m gap</Tag>
              : <span>{gap}m gap</span>
          )}
        </div>
      );
      return sPop(r, violation, display,
        `${hrsLbl}: ${icHrs}h | Gap: ${gap ?? 0}m (Reg/OT/Hol1.5 vs logged-in time) on ${dOf(r)?.format('M/D/YY (ddd)')}`);
    },
  },
  {
    title: 'Sched Hrs', key: 'SchedHrs', width: 90, align: 'right',
    render: (_, r) => {
      const total = r.Schedules?.reduce((s, x) => s + (x.SchedHrs ?? 0), 0);
      return total ? Number(total).toFixed(2) : '—';
    },
  },
  {
    title: 'Worked', dataIndex: 'Worked', key: 'Worked', width: 80, align: 'right',
    render: (v, r) => sPop(r, v != null && v !== 0, <span>{fmtHrs(v)}</span>,
      `Worked: ${fmtHrs(v)} hrs on ${dOf(r)?.format('M/D/YY (ddd)')}`),
  },
  {
    title: 'PTO', dataIndex: 'PTO', key: 'PTO', width: 70, align: 'right',
    render: (v, r) => sPop(r, v != null && v !== 0, <span>{fmtHrs(v)}</span>,
      `PTO: ${fmtHrs(v)} hrs on ${dOf(r)?.format('M/D/YY (ddd)')}`),
  },
  {
    title: 'Other', dataIndex: 'Other', key: 'Other', width: 70, align: 'right',
    render: (v, r) => sPop(r, v != null && v !== 0, <span>{fmtHrs(v)}</span>,
      `Other: ${fmtHrs(v)} hrs on ${dOf(r)?.format('M/D/YY (ddd)')}`),
  },
  ];
}

// ── Other Stats columns ───────────────────────────────────────────────────────
function buildOtherCols(navigate, currentUser, date, setComplianceModal, thresholds = {}) {
  const osPop = (row, label, rawValue, formatted) => {
    if (rawValue == null) return dash;
    if ((currentUser?.CanEditIncidents ?? 0) < 2) return <span>{formatted}</span>;
    const desc = `CCaaS: ${label} - ${formatted} on ${date.format('M/D/YY (ddd)')}`;
    const popContent = (
      <div style={{ fontSize: 13 }}>
        <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
          onClick={() => setComplianceModal({
            agent: { UserID: row.UserID, DisplayName: row.AgentName },
            day: date,
            description: desc,
          })}>
          Create Compliance Incident
        </Button>
      </div>
    );
    return (
      <Popover content={popContent} trigger="hover" mouseLeaveDelay={0.3}>
        <span style={{ cursor: 'pointer' }}>{formatted}</span>
      </Popover>
    );
  };

  const threshCell = (metricKey, v, row, label) => {
    if (v == null) return dash;
    const thresh    = thresholds[metricKey];
    const violation = thresh != null && v > thresh.MaxValue;
    const display   = violation
      ? <Tag color="red" style={{ margin: 0, fontWeight: 700 }}>{v}</Tag>
      : T(v);
    return osPop(row, label, v, display);
  };

  return [
    {
      title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', fixed: 'left', width: 160,
      render: (v, r) => (
        <AgentLink name={v} jobName={r.JobName} url={`/agents?user_id=${r.UserID}&tab=ccaas-status&ccaas_view=timeline&date=${date?.format('YYYY-MM-DD')}`} navigate={navigate} canNavigate={(currentUser?.CanViewAgentSummary ?? 0) >= 1} />
      ),
    },
    { title: 'Last 15 mins spent in Break',   dataIndex: 'Last15MinsInBreak',  key: 'Last15MinsInBreak',  width: 160, align: 'right',
      render: (v, row) => threshCell('Last15MinsInBreak',  v, row, 'Last 15 mins spent in Break') },
    { title: 'Last 15 mins spent in Non-ACD', dataIndex: 'Last15MinsInNonACD', key: 'Last15MinsInNonACD', width: 180, align: 'right',
      render: (v, row) => threshCell('Last15MinsInNonACD', v, row, 'Last 15 mins spent in Non-ACD') },
    {
      title: 'Last IB ACW', dataIndex: 'LastACW', key: 'LastACW', width: 90, align: 'right',
      render: (v, row) => {
        const f = fmtTimeStr(v);
        if (f == null) return dash;
        const lastSecs   = timeStrToSecs(v);
        const avgSecs    = row.IB_Phone_Avg_ACW_Sec;
        const isHigh     = lastSecs != null && avgSecs != null && avgSecs > 0 && lastSecs >= avgSecs * 2;
        const cell       = <span style={{ color: isHigh ? '#ff4d4f' : undefined, fontWeight: isHigh ? 700 : undefined }}>{f}</span>;
        const hasCreate  = (currentUser?.CanEditIncidents ?? 0) >= 2;
        const hasContact = row.LastACWContactID != null;
        if (!hasCreate && !hasContact) return cell;
        const desc = `CCaaS: Last IB ACW - ${f} on ${date.format('M/D/YY (ddd)')}`;
        const popContent = (
          <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {hasCreate && (
              <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
                onClick={() => setComplianceModal({
                  agent: { UserID: row.UserID, DisplayName: row.AgentName },
                  day: date,
                  description: desc,
                })}>
                Create Compliance Incident
              </Button>
            )}
            {hasContact && (
              <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
                onClick={() => navigate(`/calls/${row.LastACWContactID}`)}>
                Contact ID: {row.LastACWContactID}
              </Button>
            )}
          </div>
        );
        return <Popover content={popContent} trigger="hover" mouseLeaveDelay={0.3}><span style={{ cursor: 'pointer' }}>{cell}</span></Popover>;
      },
    },
    { title: 'IB PH Handled',      dataIndex: 'IB_PH_Handled',       key: 'IB_PH_Handled',       width: 90,  align: 'right', render: (v, row) => osPop(row, 'IB PH Handled',   v, T(v)) },
    { title: 'IB Chat Handled',    dataIndex: 'IB_Chat_Handled',     key: 'IB_Chat_Handled',     width: 95,  align: 'right', render: (v, row) => osPop(row, 'IB Chat Handled', v, T(v)) },
    { title: 'Refused Qty',        dataIndex: 'Refused_Qty',         key: 'Refused_Qty',         width: 80,  align: 'right', render: (v, row) => osPop(row, 'Refused Qty',     v, T(v)) },
    { title: 'IB Avg Talk',        dataIndex: 'IB_Phone_Avg_Talk_Sec', key: 'IB_Phone_Avg_Talk_Sec', width: 85, align: 'right', render: (v, row) => osPop(row, 'IB Phone Avg Talk', v, TS(v)) },
    { title: 'IB Avg ACW',         dataIndex: 'IB_Phone_Avg_ACW_Sec',  key: 'IB_Phone_Avg_ACW_Sec',  width: 80, align: 'right', render: (v, row) => osPop(row, 'IB Phone Avg ACW',  v, TS(v)) },
    { title: 'IB Avg HT',          dataIndex: 'IB_Phone_Avg_HT_Sec',   key: 'IB_Phone_Avg_HT_Sec',   width: 75, align: 'right', render: (v, row) => osPop(row, 'IB Phone Avg HT',   v, TS(v)) },
    { title: 'IB Max ACW',         dataIndex: 'IB_Phone_Max_ACW_Sec',  key: 'IB_Phone_Max_ACW_Sec',  width: 80, align: 'right', render: (v, row) => osPop(row, 'IB Phone Max ACW',  v, TS(v)) },
    { title: 'OB To Support',  dataIndex: 'OB_To_Support',        key: 'OB_To_Support',        width: 90,  align: 'right', render: (v, row) => osPop(row, 'OB To Support',   v, T(v)) },
    { title: 'OB To Tier2',    dataIndex: 'OB_To_Tier2',          key: 'OB_To_Tier2',          width: 85,  align: 'right', render: (v, row) => osPop(row, 'OB To Tier2',     v, T(v)) },
    { title: 'Qty Holds',      dataIndex: 'Qty_Holds',            key: 'Qty_Holds',            width: 80,  align: 'right', render: (v, row) => osPop(row, 'Qty Holds',       v, T(v)) },
    { title: 'Total Hold',     dataIndex: 'Total_Hold_Time_Sec',  key: 'Total_Hold_Time_Sec',  width: 85,  align: 'right', render: (v, row) => osPop(row, 'Total Hold Time', v, TS(v)) },
    { title: 'OB L1 Max ACW',  dataIndex: 'OB_Leg1_Max_ACW_Sec', key: 'OB_Leg1_Max_ACW_Sec',  width: 100, align: 'right', render: (v, row) => osPop(row, 'OB Leg1 Max ACW', v, TS(v)) },
    { title: 'OB Max ACW',     dataIndex: 'OB_Max_ACW_Sec',       key: 'OB_Max_ACW_Sec',       width: 90,  align: 'right', render: (v, row) => osPop(row, 'OB Max ACW',      v, TS(v)) },
  ];
}

export default function DBALPage() {
  const currentUser = useContext(UserContext);
  const navigate = useNavigate();

  // ── Shared filters ────────────────────────────────────────────────────────
  const [view,   setView]   = useState('schedule');
  const [teams,  setTeams]  = useState([]);
  const [teamId, setTeamId] = useState(null);
  const [date,   setDate]   = useState(() => dayjs().subtract(1, 'day'));

  // ── Schedule tab ──────────────────────────────────────────────────────────
  const [schedRows,    setSchedRows]    = useState([]);
  const [schedLoading, setSchedLoading] = useState(false);

  // ── Max State tab ─────────────────────────────────────────────────────────
  const [teamAgents,      setTeamAgents]      = useState([]);
  const [ccaasRaw,        setCcaasRaw]        = useState([]);
  // AWS agent states for the day, from CNEDailyStats. Unlike CCaaS there is no
  // occurrence count and no start/end -- the source records one duration per
  // state per day, and nothing about how it was reached.
  const [awsStateRows,    setAwsStateRows]    = useState([]);
  const [awsStates,       setAwsStates]       = useState([]);
  // label -> CNEDailyStats column. Threshold config points at the column, but
  // the pivot is keyed by label, so this is what joins the two.
  const [awsColumnMap,    setAwsColumnMap]    = useState({});
  const [awsLoading,      setAwsLoading]      = useState(false);
  const [ccaasLoading,    setCcaasLoading]    = useState(false);
  const [ccaasStatuses,   setCcaasStatuses]   = useState([]);
  const [hideNoData,      setHideNoData]      = useState(true);
  const [complianceModal, setComplianceModal] = useState(null);

  // ── Other Stats tab ───────────────────────────────────────────────────────
  const [otherRows,      setOtherRows]      = useState([]);
  const [otherLoading,   setOtherLoading]   = useState(false);
  const [dbalThresholds, setDbalThresholds] = useState([]);

  // ── Job filter ────────────────────────────────────────────────────────────
  const [jobFilter,      setJobFilter]      = useState([]);
  const [flaggedOnly,    setFlaggedOnly]    = useState(false);
  // The two vs Schedule views: drop agents with no shift that day.
  const [hideUnscheduled, setHideUnscheduled] = useState(false);

  // ── Load teams + thresholds once ──────────────────────────────────────────
  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1, no_client_users: 1 } })
      .then((r) => setTeams(r.data || []))
      .catch(() => {});
    api.get('/api/admin/ccaas-statuses')
      .then((r) => setCcaasStatuses(r.data || []))
      .catch(() => {});
    api.get('/api/admin/dbal-thresholds')
      .then((r) => setDbalThresholds(r.data || []))
      .catch(() => {});
  }, []);

  // ── Default to current user's team only if it exists in the list ──────────
  useEffect(() => {
    if (teamId || !teams.length || !currentUser?.TeamID) return;
    const mine = teams.find((t) => t.ID === currentUser.TeamID);
    if (mine) setTeamId(mine.ID);
  }, [teams, currentUser, teamId]);

  // ── Load team agents for Max State pivot ──────────────────────────────────
  useEffect(() => {
    if (!teamId) { setTeamAgents([]); return; }
    api.get('/api/agents')
      .then((r) => {
        const filtered = (r.data || []).filter((a) => a.TeamID === teamId);
        setTeamAgents(filtered.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName)));
      })
      .catch(() => {});
  }, [teamId]);

  // ── Fetch schedule ────────────────────────────────────────────────────────
  // Both schedule views share this: the schedule, punches and timecard hours
  // are the same, and only the login source differs. Keyed on the view so
  // switching between them refetches rather than showing one source's sessions
  // under the other's heading.
  const schedSource = view === 'aws-schedule' ? 'aws' : 'max';

  const loadSchedule = useCallback(async () => {
    if (!teamId || !date) return;
    setSchedLoading(true);
    try {
      const res = await api.get('/api/dbal/schedule', {
        params: { team_id: teamId, date: date.format('YYYY-MM-DD'), source: schedSource },
      });
      setSchedRows(res.data || []);
    } catch {
      setSchedRows([]);
    } finally {
      setSchedLoading(false);
    }
  }, [teamId, date, schedSource]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  // ── Fetch CCaaS daily ─────────────────────────────────────────────────────
  const loadCcaas = useCallback(async () => {
    if (!teamId || !date) return;
    setCcaasLoading(true);
    try {
      const res = await api.get('/api/dbal/ccaas-daily', {
        params: { team_id: teamId, date: date.format('YYYY-MM-DD') },
      });
      const d = res.data;
      setCcaasRaw(Array.isArray(d) ? d : Array.isArray(d?.rows) ? d.rows : []);
    } catch {
      setCcaasRaw([]);
    } finally {
      setCcaasLoading(false);
    }
  }, [teamId, date]);

  useEffect(() => { loadCcaas(); }, [loadCcaas]);

  // ── Fetch AWS state daily ─────────────────────────────────────────────────
  const loadAwsState = useCallback(async () => {
    if (!teamId || !date) return;
    setAwsLoading(true);
    try {
      const res = await api.get('/api/dbal/aws-state-daily', {
        params: { team_id: teamId, date: date.format('YYYY-MM-DD') },
      });
      setAwsStateRows(res.data?.rows || []);
      setAwsStates(res.data?.states || []);
      setAwsColumnMap(res.data?.columns || {});
    } catch {
      setAwsStateRows([]);
      setAwsStates([]);
      setAwsColumnMap({});
    } finally {
      setAwsLoading(false);
    }
  }, [teamId, date]);

  useEffect(() => { loadAwsState(); }, [loadAwsState]);

  // ── Fetch Other Stats ─────────────────────────────────────────────────────
  const loadOther = useCallback(async () => {
    if (!teamId || !date) return;
    setOtherLoading(true);
    try {
      const res = await api.get('/api/dbal/other-daily', {
        params: { team_id: teamId, date: date.format('YYYY-MM-DD') },
      });
      setOtherRows(res.data || []);
    } catch {
      setOtherRows([]);
    } finally {
      setOtherLoading(false);
    }
  }, [teamId, date]);

  useEffect(() => { loadOther(); }, [loadOther]);

  // ── Threshold map ─────────────────────────────────────────────────────────
  const thresholdMap = useMemo(() => {
    const global = {};
    const team   = {};
    ccaasStatuses.forEach((s) => {
      if (s.TeamID == null) global[s.State] = s;
      else if (s.TeamID === teamId) team[s.State] = s;
    });
    return { ...global, ...team };
  }, [ccaasStatuses, teamId]);


  // ── Max State pivot ───────────────────────────────────────────────────────
  const { pivotRows, ccaasStates } = useMemo(() => {
    const allStates     = [...new Set(ccaasRaw.map((r) => r.State))];
    const orderedStates = [
      ...PRIORITY.filter((s) => allStates.includes(s)),
      ...allStates.filter((s) => !PRIORITY.includes(s)).sort(),
    ];
    const dataMap = {};
    ccaasRaw.forEach((r) => {
      if (!dataMap[r.UserID]) dataMap[r.UserID] = {};
      dataMap[r.UserID][r.State] = r;
    });
    const rows = teamAgents.map((a) => ({
      UserID:      a.ID,
      DisplayName: a.DisplayName,
      JobName:     a.JobName,
      _hasData:    !!dataMap[a.ID],
      ...(dataMap[a.ID] || {}),
    }));
    return { pivotRows: rows, ccaasStates: orderedStates };
  }, [ccaasRaw, teamAgents]);

  const visibleRows = hideNoData ? pivotRows.filter((r) => r._hasData) : pivotRows;

  // WorkedHrs (in hours) per agent from schedule data — used for tiered break thresholds
  const workedMinsMap = useMemo(() => {
    const map = {};
    schedRows.forEach((r) => { if (r.UserID && r.Worked != null) map[r.UserID] = r.Worked * 60; });
    return map;
  }, [schedRows]);

  const awsVisibleRows = hideNoData ? awsStateRows.filter((r) => r._hasData) : awsStateRows;

  // Same precedence as the CCaaS map: a team row overrides the all-teams one.
  // Rows with no AWSColumn are CCaaS-only and simply never match here.
  const awsThresholdMap = useMemo(() => {
    const global = {};
    const team   = {};
    ccaasStatuses.forEach((s) => {
      if (!s.AWSColumn) return;
      if (s.TeamID == null) global[s.AWSColumn] = s;
      else if (s.TeamID === teamId) team[s.AWSColumn] = s;
    });
    return { ...global, ...team };
  }, [ccaasStatuses, teamId]);

  const awsThreshFor = (label) => awsThresholdMap[awsColumnMap[label]];

  const awsCols = buildAwsStateCols({
    navigate, currentUser, date, setComplianceModal, states: awsStates, threshFor: awsThreshFor,
    workedMinsFor: (row) => workedMinsMap[row.UserID] ?? null,
  });

  const pivotCols = [
    {
      title: 'Agent', dataIndex: 'DisplayName', key: 'agent', width: 200, fixed: 'left',
      sorter: (a, b) => a.DisplayName.localeCompare(b.DisplayName),
      render: (name, row) => {
        const d = date?.format('YYYY-MM-DD');
        return (
          <div>
            <AgentLink name={name} jobName={row.JobName} url={`/agents?user_id=${row.UserID}&tab=ccaas-status&ccaas_view=timeline&date=${d}`} navigate={navigate} canNavigate={(currentUser?.CanViewAgentSummary ?? 0) >= 1} />
          </div>
        );
      },
    },
    ...ccaasStates.map((state) => {
      const thresh = thresholdMap[state];
      return {
        title: state, key: state, width: 110, align: 'right',
        sorter: (a, b) => (a[state]?.TotalMins ?? -1) - (b[state]?.TotalMins ?? -1),
        render: (_, row) => {
          const cell = row[state];
          if (!cell) return <span style={{ color: '#d9d9d9' }}>—</span>;
          const mins = cell.TotalMins;
          const cnt  = cell.OccurrenceCount;
          const violation = exceedsMins(thresh, mins, workedMinsMap[row.UserID] ?? null)
            || ((thresh?.Tiers || []).length === 0
                && thresh?.MaxCountPerDay != null && cnt > thresh.MaxCountPerDay);
          const tag = (
            <Tag color={violation ? 'red' : undefined}
              style={{ margin: 0, cursor: (currentUser?.CanEditIncidents ?? 0) >= 2 ? 'pointer' : 'default', fontWeight: violation ? 700 : undefined }}>
              {mins}m{cnt > 1 ? ` (${cnt}x)` : ''}
            </Tag>
          );
          const fmtT = (hms) => { if (!hms) return null; const [h, m] = hms.split(':'); const hr = parseInt(h, 10); return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`; };
          const popContent = (
            <div style={{ fontSize: 13 }}>
              {cell.EarliestStart && <div><span style={{ color: '#8c8c8c' }}>From:</span> {fmtT(cell.EarliestStart)}</div>}
              {cell.LatestEnd     && <div><span style={{ color: '#8c8c8c' }}>To:</span> {fmtT(cell.LatestEnd)}</div>}
              {(currentUser?.CanEditIncidents ?? 0) >= 2 && (
                <Button size="small" type="link" style={{ padding: 0, height: 'auto', marginTop: cell.EarliestStart ? 4 : 0 }}
                  onClick={() => setComplianceModal({
                    agent:       { UserID: row.UserID, DisplayName: row.DisplayName },
                    day:         date,
                    description: `CCaaS: ${state} - ${mins} min on ${date.format('M/D/YY (ddd)')}`,
                  })}>
                  Create Compliance Incident
                </Button>
              )}
            </div>
          );
          return (
            <Popover content={popContent} trigger="hover" mouseLeaveDelay={0.3}>
              {tag}
            </Popover>
          );
        },
      };
    }),
  ];

  // DBAL other-stats threshold map: metric -> { MaxValue, ValueType }, team-specific overrides global
  const dbalThresholdMap = useMemo(() => {
    const global = {};
    const team   = {};
    dbalThresholds.forEach((t) => {
      if (t.TeamID == null) global[t.Metric] = t;
      else if (t.TeamID === teamId) team[t.Metric] = t;
    });
    return { ...global, ...team };
  }, [dbalThresholds, teamId]);

  const otherCols = buildOtherCols(navigate, currentUser, date, setComplianceModal, dbalThresholdMap);

  // ── Job filter options (derived from loaded data) ─────────────────────────
  const jobOptions = useMemo(() => {
    const names = new Set();
    [...schedRows, ...pivotRows, ...otherRows].forEach((r) => { if (r.JobName) names.add(r.JobName); });
    return [...names].sort().map((n) => ({ label: n, value: n }));
  }, [schedRows, pivotRows, otherRows]);

  const isMaxStateFlagged = (row) => ccaasStates.some((state) => {
    const cell = row[state];
    if (!cell) return false;
    const thresh = thresholdMap[state];
    if (!thresh) return false;
    return exceedsMins(thresh, cell.TotalMins, workedMinsMap[row.UserID] ?? null)
        || ((thresh.Tiers || []).length === 0
            && thresh.MaxCountPerDay != null && cell.OccurrenceCount > thresh.MaxCountPerDay);
  });

  const isAwsFlagged = (row) => awsStates.some(
    (state) => exceedsMins(awsThreshFor(state), row[state], workedMinsMap[row.UserID] ?? null));
  const isOtherFlagged = (row) => Object.entries(dbalThresholdMap).some(([metric, t]) => {
    const v = row[metric];
    return v != null && t.MaxValue != null && v > t.MaxValue;
  });

  const filteredSchedRows   = schedRows
    .filter(r => !jobFilter.length || jobFilter.includes(r.JobName))
    .filter(r => !flaggedOnly      || isScheduleFlagged(r, dbalThresholdMap, schedSource))
    .filter(r => !hideUnscheduled  || r.Schedules?.length > 0);
  const filteredVisibleRows = (jobFilter.length ? visibleRows.filter((r) => jobFilter.includes(r.JobName)) : visibleRows)
    .filter(r => !flaggedOnly || isMaxStateFlagged(r));
  const filteredOtherRows   = (jobFilter.length ? otherRows.filter((r) => jobFilter.includes(r.JobName)) : otherRows)
    .filter(r => !flaggedOnly || isOtherFlagged(r));
  const filteredAwsRows     = (jobFilter.length ? awsVisibleRows.filter((r) => jobFilter.includes(r.JobName)) : awsVisibleRows)
    .filter(r => !flaggedOnly || isAwsFlagged(r));

  // ── Export helpers ────────────────────────────────────────────────────────
  const xlsxDownload = (rows, filename) => {
    if (!rows.length) { message.warning('No data to export.'); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, filename);
  };

  const exportSchedule = () => {
    const rows = filteredSchedRows.map((r) => ({
      'Agent':       r.AgentName,
      'Job':         r.JobName ?? '',
      'Shift':       r.Schedules?.map((s) => s.Shift || '').join(', ') ?? '',
      'Sched Start': r.Schedules?.map((s) => fmtPunch(s.SchedStart)).join(', ') ?? '',
      'Sched End':   r.Schedules?.map((s) => fmtPunch(s.SchedEnd)).join(', ') ?? '',
      [schedSource === 'aws' ? 'SFDC Login' : 'IC Login']:
        r.LoginSessions?.map(s => s.login).join(', ') ?? '',
      // Omitted on the AWS source, matching the table: the column is not there
      // to export, and a sheet of dashes is worse than a sheet without it.
      ...(schedSource === 'aws' ? {
        'AWS Lunch Mins': r.LunchMins == null ? '' : Math.round(r.LunchMins),
      } : {
        'IC Available': r.LoginSessions?.map(s => s.available ?? '—').join(', ') ?? '',
      }),
      [schedSource === 'aws' ? 'SFDC Logout' : 'IC Logout']:
        r.LoginSessions?.map(s => s.logout ?? '—').join(', ') ?? '',
      'Punches':     r.Punches?.sort((a, b) => (a.in || '').localeCompare(b.in || '')).map((p) => `${p.in?.slice(0,5) ?? '?'} - ${p.out?.slice(0,5) ?? '?'}`).join(', ') ?? '',
      'Sched Hrs':   r.Schedules?.reduce((s, x) => s + (x.SchedHrs ?? 0), 0) || '',
      'Worked':      r.Worked ?? '',
      'PTO':         r.PTO    ?? '',
      'Other':       r.Other  ?? '',
    }));
    xlsxDownload(rows, `dbal_${schedSource}_vs_schedule_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  const exportMaxState = () => {
    const rows = filteredVisibleRows.map((r) => {
      const row = { 'Agent': r.DisplayName, 'Job': r.JobName ?? '' };
      ccaasStates.forEach((state) => { row[state] = r[state]?.TotalMins ?? ''; });
      return row;
    });
    xlsxDownload(rows, `dbal_max_state_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  const exportAwsState = () => {
    const rows = filteredAwsRows.map((r) => {
      const row = {
        'Agent':     r.DisplayName,
        'Job':       r.JobName ?? '',
        'Contacts':  r.TotalContactsHandled ?? '',
        'Occupancy': r.OccupancyPct != null ? Number(r.OccupancyPct) : '',
      };
      // Minutes, matching the screen. A spreadsheet can sum or average them;
      // a formatted duration could not be.
      awsStates.forEach((state) => { row[state] = r[state] ?? ''; });
      return row;
    });
    xlsxDownload(rows, `dbal_aws_state_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  const exportOther = () => {
    const rows = filteredOtherRows.map((r) => ({
      'Agent':             r.AgentName,
      'Job':               r.JobName ?? '',
      'Last 15 mins spent in Break':   r.Last15MinsInBreak  ?? '',
      'Last 15 mins spent in Non-ACD': r.Last15MinsInNonACD ?? '',
      'IB PH Handled':     r.IB_PH_Handled      ?? '',
      'IB Chat Handled':   r.IB_Chat_Handled     ?? '',
      'Refused Qty':       r.Refused_Qty         ?? '',
      'IB Avg Talk':       fmtSecs(r.IB_Phone_Avg_Talk_Sec) ?? '',
      'IB Avg ACW':        fmtSecs(r.IB_Phone_Avg_ACW_Sec)  ?? '',
      'IB Avg HT':         fmtSecs(r.IB_Phone_Avg_HT_Sec)   ?? '',
      'IB Max ACW':        fmtSecs(r.IB_Phone_Max_ACW_Sec)  ?? '',
      'Last IB ACW':       r.LastACW ? (fmtTimeStr(r.LastACW) ?? '') : '',
      'OB To Support':     r.OB_To_Support       ?? '',
      'OB To Tier2':       r.OB_To_Tier2         ?? '',
      'Qty Holds':         r.Qty_Holds           ?? '',
      'Total Hold':        fmtSecs(r.Total_Hold_Time_Sec)   ?? '',
      'OB L1 Max ACW':     fmtSecs(r.OB_Leg1_Max_ACW_Sec)  ?? '',
      'OB Max ACW':        fmtSecs(r.OB_Max_ACW_Sec)        ?? '',
    }));
    xlsxDownload(rows, `dbal_other_stats_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>DBAL</Typography.Title>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: 'Max vs Schedule',      value: 'schedule'    },
            { label: 'Max State by Day',     value: 'max-state'   },
            { label: 'Max Other Stats by Day', value: 'other-stats' },
            // Beside AWS State by Day rather than beside its own twin: the
            // toggle reads as the two sources, Max then AWS, not as two
            // schedule views separated by everything else.
            { label: 'AWS vs Schedule',      value: 'aws-schedule' },
            { label: 'AWS State by Day',     value: 'aws-state'   },
          ]}
        />
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          placeholder="Select team"
          value={teamId}
          onChange={setTeamId}
          options={teams.map((t) => ({ label: t.Name, value: t.ID }))}
          style={{ minWidth: 220 }}
        />
        <DatePicker
          value={date}
          onChange={(v) => setDate(v ?? dayjs().subtract(1, 'day'))}
          format="YYYY-MM-DD"
          allowClear={false}
        />
        <Select
          mode="multiple"
          placeholder="All jobs"
          value={jobFilter}
          onChange={setJobFilter}
          options={jobOptions}
          allowClear
          maxTagCount="responsive"
          style={{ minWidth: 160 }}
        />
        <Checkbox checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)}>
          Flagged only
        </Checkbox>
        {(view === 'schedule' || view === 'aws-schedule') && (
          <Checkbox checked={hideUnscheduled} onChange={(e) => setHideUnscheduled(e.target.checked)}>
            Hide unscheduled
          </Checkbox>
        )}
        {(view === 'max-state' || view === 'aws-state') && (
          <Checkbox checked={hideNoData} onChange={(e) => setHideNoData(e.target.checked)}>
            Hide agents with no data
          </Checkbox>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={
            view === 'schedule' || view === 'aws-schedule' ? exportSchedule
            : view === 'max-state' ? exportMaxState
            : view === 'aws-state' ? exportAwsState
            : exportOther
          }
          disabled={!teamId}
        >
          Export
        </Button>
      </Space>

      {(view === 'schedule' || view === 'aws-schedule') && (
        <Table
          rowKey="UserID"
          dataSource={filteredSchedRows}
          columns={buildScheduleCols(navigate, currentUser, date, setComplianceModal, dbalThresholdMap, schedSource)}
          loading={schedLoading}
          size="small"
          scroll={{ x: 'max-content' }}
          sticky
          pagination={false}
          locale={{ emptyText: teamId ? 'No data for selected team and date.' : 'Select a team to view data.' }}
        />
      )}

      {view === 'max-state' && (
        <Table
          rowKey="UserID"
          dataSource={filteredVisibleRows}
          columns={pivotCols}
          loading={ccaasLoading}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          sticky
          bordered
          locale={{ emptyText: teamId ? 'No CCaaS data for this team and date.' : 'Select a team to view data.' }}
        />
      )}

      {view === 'aws-state' && (
        <Table
          rowKey="UserID"
          dataSource={filteredAwsRows}
          columns={awsCols}
          loading={awsLoading}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          sticky
          bordered
          locale={{ emptyText: teamId ? 'No AWS state data for this team and date.' : 'Select a team to view data.' }}
        />
      )}

      {view === 'other-stats' && (
        <Table
          rowKey="UserID"
          dataSource={filteredOtherRows}
          columns={otherCols}
          loading={otherLoading}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={false}
          sticky
          bordered
          locale={{ emptyText: teamId ? 'No data for this team and date.' : 'Select a team to view data.' }}
        />
      )}

      <ComplianceCreateModal
        open={!!complianceModal}
        agentUserId={complianceModal?.agent?.UserID}
        agentName={complianceModal?.agent?.DisplayName}
        initialDate={complianceModal?.day}
        initialDescription={complianceModal?.description}
        initialTeamId={teamId}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setComplianceModal(null)}
        onCreated={() => message.success(`Compliance incident created for ${complianceModal?.agent?.DisplayName}.`)}
      />
    </>
  );
}
